import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import * as R from 'ramda';

import { CiModuleInspection, CiModuleInspectionUpdate } from '@atom/types/form';
import { MediaItem } from '@atom/types/media';

import CiCommentsModal from './CiCommentsModal';
import CiInspectionChoiceModal from './CiInspectionChoiceModal';
import CiMediaModal from './CiMediaModal';

import classes from './ci.module.css';

interface Props {
  assetId: string;
  fieldId: string;
  inspection: CiModuleInspection;
  inspectionIndex: number;
  media: MediaItem[];
  preview: boolean;
  onChange: (update: CiModuleInspectionUpdate) => void;
  uploadFiles: (
    files?: any[],
    subjectId?: string,
    subjectType?: string,
    inspectionUpdate?: CiModuleInspectionUpdate,
  ) => void;
  renameMedia: (mediaId: string, subjectId: string, name: string) => void;
  isEditable: boolean;
}

const getChoice = ({ choices }: CiModuleInspection, rating: string) => {
  return rating ? choices.find(choice => choice.rating === rating) : null;
};

const CiFieldRow = ({
  assetId,
  fieldId,
  inspection,
  inspectionIndex,
  media,
  preview,
  onChange,
  uploadFiles,
  renameMedia,
  isEditable,
}: Props) => {
  const { name } = inspection;
  const { state } = useLocation();

  const [open, setOpen] = useState<boolean>(false);
  const [rating, setRating] = useState<string>(inspection.rating || '');

  useEffect(() => {
    if (preview) {
      setRating('');
    } else {
      setRating(inspection?.rating);
    }
  }, [inspection, preview]);

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleChoice = (rating: string) => {
    setRating(rating);
    onChange({ fieldId, inspectionIndex, name, rating });
    setOpen(false);
  };

  const handleComments = (comments: string) => {
    onChange({ fieldId, inspectionIndex, name, comments });
  };

  const handleUploadFiles = (
    files?: any[],
    subjectId?: string,
    subjectType?: string,
  ) => {
    const mediaIds = media.map(({ id }) => id);
    const inspectionUpdate = { fieldId, inspectionIndex, name, mediaIds };
    uploadFiles(files, subjectId, subjectType, inspectionUpdate);
  };

  const handleMediaChange = (mediaIds: string[]) => {
    onChange({ fieldId, inspectionIndex, name, mediaIds });
  };

  const choice = getChoice(inspection, rating);

  const isWorkOrderClosed = state?.workOrderClosed;

  return (
    <>
      <div className={classes.tableRow}>
        <div className={classes.largeColumn}>{inspection.name}</div>
        <div
          className={classes.descriptionColumn}
          onClick={() => isEditable && !isWorkOrderClosed && setOpen(true)}
        >
          <div className={classes.descriptionRow}>
            {choice?.description || 'Select'}
          </div>
        </div>
        <div className={clsx(classes.normalColumn, classes.rating)}>
          {choice?.rating || ''}
        </div>
        <div className={clsx(classes.smallColumn, classes.points)}>
          {R.isNil(choice?.points) ? '' : choice.points}
        </div>
        <div className={classes.smallColumn}>
          <CiMediaModal
            media={media}
            onChange={handleMediaChange}
            uploadFiles={handleUploadFiles}
            renameMedia={renameMedia}
            assetId={assetId}
            disabled={preview || isWorkOrderClosed}
            isEditable={isEditable}
          />
        </div>
        <div className={classes.smallColumn}>
          <CiCommentsModal
            inspection={inspection}
            onSubmit={handleComments}
            preview={preview}
            isWorkOrderClosed={isWorkOrderClosed}
            isEditable={isEditable}
          />
        </div>
      </div>
      <CiInspectionChoiceModal
        inspection={inspection}
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleChoice}
      />
    </>
  );
};

export default CiFieldRow;
